import { useEffect, useRef, useState } from "react";
import { RootState } from "redux/store";
import { Alert, Button, Card, Col, Form, ListGroup, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Title, Table, Modal, TooltipIcon } from "components";
import { Link, useNavigate } from "react-router-dom";
import { getOwners } from "redux/slices/Owners";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getStores } from "redux/slices/Stores";
import { GetMultiStoreStores, GetViewableStores, MultiStoreTest, UpdateMultiStore } from "redux/slices/MultiStore";
import { showToast } from "redux/slices/Toasts";
import { ZCompactSearchBar } from "components/ZCompactSearchBar";
import { onlyUnique } from "helpers/Functions";
import { ZButton } from "components/ZButton";
import { ZTooltip } from "components/ZTooltip";

export function AdvancedMultiStore() {
	const MAX_NUMBER_SELECTED = 20;
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [filteredStores, setFilteredStores] = useState<any[]>([]);
	const [selectedStoreList, setSelectedStoreList] = useState<any[]>([]);
	const [storeFilter, setStoreFilter] = useState<string>('');

	//Main Store and View Stores
	const [multistoreGroup, setMultistoreGroup] = useState<any[]>([]);
	const [viewMultistoreGroup, setViewMultistoreGroup] = useState<any[]>([]);

	const [testResult, setTestResult] = useState<any[]>([]);

	const [showPrompt, setShowPrompt] = useState<boolean>(false);
	const [showDifferentOwnerPrompt, setShowDifferentOwnerPrompt] = useState<boolean>(false);
	const differentOwnersPromptAction = useRef<() => void>();

	const [addSelectedButtonEnabled, setAddSelectedButtonEnabled] = useState<boolean>(false);
	const [addSelectedButtonError, setAddSelectedButtonError] = useState<string>("");

	const [comment, setComment] = useState<string | null>(null);
	const [stores, setStores] = useState<any[]>([]);

	const selectAllRef = useRef<any>(null);

	function reload(){
		dispatch(GetMultiStoreStores()).then((result) => {
			setStores(result.data);
		});
	}
	useEffect(() => {
		reload();
	}, []);

	useEffect(() => {
		onFilterChange(storeFilter);
	}, [stores])

	useEffect(() => {
		if (storeFilter == "" || storeFilter == null) {
			setAddSelectedButtonEnabled(false);
			setAddSelectedButtonError("Can't add selected without applying a search filter");
		} else if (selectedStoreList.length > MAX_NUMBER_SELECTED) {
			setAddSelectedButtonEnabled(false);
			setAddSelectedButtonError(`Can't add more than ${MAX_NUMBER_SELECTED} items at a time`);
		} else if (selectedStoreList.length === 0) {
			setAddSelectedButtonEnabled(false);
			setAddSelectedButtonError(`Must select at least 1 store to add`);
		} else {
			setAddSelectedButtonEnabled(true);
			setAddSelectedButtonError("");
		}
	}, [storeFilter, selectedStoreList]);

	useEffect(() => {
		if (selectAllRef.current) {
			selectAllRef.current.indeterminate = selectedStoreList.length > 0 && selectedStoreList.length != filteredStores.filter(s => s.viewableStoreIds.length === 0).length;
		}
	}, [selectedStoreList]);

	function onFilterChange(value: string) {
		setSelectedStoreList([]);
		var ids = value.split(' ').join(',').split(',');
		let ss: any[] = value.trim() === "" ? [] : stores.filter((i: any) => i.name.toLowerCase().includes(value.toLowerCase()) || ids.includes(i.id.toString()));
		setFilteredStores(ss);
		setStoreFilter(value);
	}

	function addtoMainStores(store: any) {
		if (!multistoreGroup.includes(store)) {
			let owners = multistoreGroup.map(i => i.ownerId);
			if (owners.length > 0 && !owners.includes(store.ownerId)) {
				differentOwnersPromptAction.current = () => setMultistoreGroup([...multistoreGroup, store]);
				setShowDifferentOwnerPrompt(true);
			}
			else {
				setMultistoreGroup([...multistoreGroup, store])
				differentOwnersPromptAction.current = undefined;
			}
		}
	}

	function addMultipleToMainStores(storeIds: number[]) {
		const newStoreIds = storeIds.filter(storeId => !multistoreGroup.find(s => s.id === storeId));
		const newStores = stores.filter(s => newStoreIds.includes(s.id));
		const newMultiStoreGroup = [...multistoreGroup, ...newStores];

		setMultistoreGroup(newMultiStoreGroup);
	}

	function addMultipleToViewStores(storeIds: number[]) {
		const newStoreIds = storeIds.filter(storeId => !viewMultistoreGroup.find(s => s.id === storeId));
		const newStores = stores.filter(s => newStoreIds.includes(s.id));
		const newViewMultiStoreGroup = [...viewMultistoreGroup, ...newStores];

		setViewMultistoreGroup(newViewMultiStoreGroup);
	}

	function addToViewStores(store: any) {
		if (!viewMultistoreGroup.includes(store)) {
			let owners = [...multistoreGroup.map(i => i.ownerId), ...viewMultistoreGroup.map(i => i.ownerId)];
			if (owners.length > 0 && !owners.includes(store.ownerId)) {
				differentOwnersPromptAction.current = () => setViewMultistoreGroup([...viewMultistoreGroup, store]);
				setShowDifferentOwnerPrompt(true);
			}
			else {
				setViewMultistoreGroup([...viewMultistoreGroup, store])
				differentOwnersPromptAction.current = undefined;
			}

		}
	}

	function removeFromMainStores(store: any) {
		if (multistoreGroup.includes(store)) {
			let temp = [...multistoreGroup];
			temp.splice(temp.indexOf(store), 1);
			setMultistoreGroup(temp)
		}
	}


	function removeFromViewStores(store: any) {
		if (viewMultistoreGroup.includes(store)) {
			let temp = [...viewMultistoreGroup];
			temp.splice(temp.indexOf(store), 1);
			setViewMultistoreGroup(temp)
		}
	}

	function testMultiStore(stores: any[]) {
		if (stores.length > 0) {
			dispatch(MultiStoreTest(stores.map(i => i.id as number))).then((result) => {
				setTestResult(result.stores);
			})
		} else {
			setTestResult([]);
		}
	}


	async function saveMultiStore() {
		let errors: any[] = [];

		if (multistoreGroup.length > 0) {
			for (let index = 0; index < multistoreGroup.length; index++) {
				const element = multistoreGroup[index];

				await dispatch(UpdateMultiStore({ storeId: element.id, viewStoreIds: viewMultistoreGroup.map(i => i.id), comment: comment })).then((result) => {
				});
			}
		}

		if (errors.length > 0) {
			dispatch(showToast({ value: errors }));
		} else {
			dispatch(showToast({ value: ["Done"] }));
			reload();
		}
	}

	function updateSelectedStoreList(storeId: number, add: boolean) {
		console.log(storeId, add);
		if (add) {
			let newList = [...selectedStoreList];
			newList.push(storeId);
			newList = newList.filter(onlyUnique);
			setSelectedStoreList(newList);
		} else {
			const newList = selectedStoreList.filter(s => s != storeId);
			setSelectedStoreList(newList);
		}
	}

	function selectAllStores() {
		setSelectedStoreList(filteredStores.map(s => s.id));
	}

	function deselectAllStores() {
		setSelectedStoreList([]);
	}

	function viewMultiStoreGroup(storeId: number) {
		dispatch(GetViewableStores(storeId)).then((result) => {
			const comments: Set<string> = new Set<string>();
			const viewableStoreIds = result.data.map(s => {
				comments.add(s.comment);
				return s.viewStoreId;
			})
			if (comments.size > 1) {
				console.error("More than 1 unique comment returned for the given store view!");
			}
			let ss: any[] = stores.filter((i: any) => viewableStoreIds.includes(i.id));
			if (ss.length > 0) {
				let commentsArr: string[] = [];
				comments.forEach((x, y, s) => {
					commentsArr.push(x);
				});
				setComment(commentsArr.length > 0 ? commentsArr[0] : null);
				setMultistoreGroup([stores.find(s => s.id === storeId)]);
				setViewMultistoreGroup(ss);
			}
		});
	}

	return (
		<>
			<Title pageTitle="Multi-Store" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col xs={4} className="d-flex flex-column">
									<h3>Search</h3>
									<ZCompactSearchBar placeholderText={"Search by site id separated by space ( ) or comma (,)"} className="ms-1 mb-2" searchText={storeFilter} onChange={(t) => onFilterChange(t)} />
									{filteredStores.filter(s => !(multistoreGroup.includes(s) && viewMultistoreGroup.includes(s))).length > 0 ?
										<><div style={{ width: "fit-content" }} className="d-flex prevent-select simple-multistore-store-btn ms-1 p-1 align-items-center mb-1"
											onClick={(e) => {
												selectAllRef.current.checked ? deselectAllStores() : selectAllStores()
											}}>
											<Form.Check ref={selectAllRef} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.target.checked ? selectAllStores() : deselectAllStores() }} checked={selectedStoreList.length != 0 && selectedStoreList.length === filteredStores.length} className="align-self-center" />
											<div className="d-flex ms-2" style={{ textAlign: "left" }}>Select all</div>
										</div>
											<hr className="mt-1 mb-1 border-1 mx-1" /></> : <></>
									}
									<div className="scrollable-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
										<Form>
											{filteredStores.filter(s => !(multistoreGroup.includes(s) && viewMultistoreGroup.includes(s))).map((store: any, index: any) => (
												<div key={index} className="d-flex prevent-select" style={{ width: "100%" }}>
													<div className={`simple-multistore-store-btn pt-1 pb-1 pe-1 d-flex mb-1 align-items-center`} style={{ flexGrow: 1 }} onClick={(e) => { updateSelectedStoreList(store.id, !selectedStoreList.some(s => s === store.id)); }}>
														<div className="ms-2">
															<Form.Check onClick={(e) => e.stopPropagation()} onChange={(e) => { updateSelectedStoreList(store.id, e.target.checked) }} checked={selectedStoreList.some(s => s === store.id)} className="align-self-center" />
														</div>
														<div className="d-flex ms-2" style={{ textAlign: "left" }}>{store.id} {store.name}</div>
														{store.viewableStoreIds.length > 0 ?
															<div className="d-flex ms-auto align-items-center">
																<div className="btn btn-light px-1 py-0"
																	style={{ background: "none" }}
																	onClick={(e) => {
																		e.stopPropagation();
																		viewMultiStoreGroup(store.id);
																	}}>
																	<i className="mdi mdi-eye"></i>
																</div>
																<TooltipIcon classes={["px-1 py-0"]} text={"This store is already part of a Multi-Store group"} placement={"right"} icon={"mdi mdi-storefront"}></TooltipIcon>
															</div> : <></>}
													</div>
												</div>
											))}
										</Form>
									</div>
								</Col>

								<Col xs={4} className="pe-0">
									<div className="d-flex align-items-center mb-2">
										<h3 className="mb-0">Main Store</h3>
									</div>
									<div className="d-flex align-items-center pt-1">
										<Button variant="danger" className="py-0 px-1" disabled={multistoreGroup.length === 0} onClick={() => { setComment(null); setMultistoreGroup([]); }}>
											Clear All
										</Button>
										<Button variant="secondary" className="ms-1 me-1 py-0 px-1" disabled={multistoreGroup.length === 0} onClick={() => testMultiStore(multistoreGroup)}>
											View Test
										</Button>
									</div>
									<div className="mt-3">
										<br/>
									</div>
									<div className="scrollable-container pe-1 me-0" style={{ maxHeight: '520px', overflowY: 'auto' }}>
										<ListGroup>
											{multistoreGroup.map((store: any, index: any) => (
												<div className="d-flex prevent-select" style={{ width: "100%" }}>
													<div key={index} className="d-flex mb-1 p-1 align-items-center" style={{ flexGrow: 1 }}>
														<div className="d-flex me-1" style={{ textAlign: "left" }}>{store.id} {store.name}</div>
														<Button variant="danger" className="ms-auto px-1 py-0" onClick={() => removeFromMainStores(store)}>
															<i className="mdi mdi-trash-can"></i>
														</Button>
													</div>
												</div>
											))}
										</ListGroup>
									</div>
								</Col>

								<Col xs={4} className="pe-0">
									<div className="d-flex align-items-center mb-2">
										<h3 className="mb-0">View Stores</h3>
									</div>
									<div className="d-flex align-items-center pt-1">
										<Button variant="danger" className="py-0 px-1" disabled={viewMultistoreGroup.length === 0} onClick={() => { setComment(null); setViewMultistoreGroup([]); }}>
											Clear All
										</Button>
										<Button variant="secondary" className="ms-1 me-1 py-0 px-1" disabled={viewMultistoreGroup.length === 0} onClick={() => testMultiStore(viewMultistoreGroup)}>
											View Test
										</Button>
									</div>
									<div className="mt-3">
										<Form.Group className="mb-2 d-flex flex-row align-items-center" style={{ width: "100%" }}>
											<Form.Label className="me-1">Comment: </Form.Label>
											<Form.Control
												required
												type="text"
												name="comment"
												id="comment"
												placeholder="Enter your name here"
												value={comment ?? ""}
												onChange={(e) => {
													setComment(e.target.value.length > 0 ? e.target.value : null);
												}}
											/>
										</Form.Group>
									</div>
									<div className="scrollable-container pe-1 me-0" style={{ maxHeight: '520px', overflowY: 'auto' }}>
										<ListGroup>
											{viewMultistoreGroup.map((store: any, index: any) => (
												<div className="d-flex prevent-select" style={{ width: "100%" }}>
													<div key={index} className="d-flex mb-1 p-1 align-items-center" style={{ flexGrow: 1 }}>
														<div className="d-flex me-1" style={{ textAlign: "left" }}>{store.id} {store.name}</div>
														<Button variant="danger" className="ms-auto px-1 py-0" onClick={() => removeFromViewStores(store)}>
															<i className="mdi mdi-trash-can"></i>
														</Button>
													</div>
												</div>
											))}
										</ListGroup>
									</div>
								</Col>
							</Row>

							<Row>
								<Col xs={4} className="ms-auto d-flex justify-content-end">
									<ZTooltip 
										enabled={!addSelectedButtonEnabled} 
										text={addSelectedButtonError} 
										placement="top" 
									>
										<ZButton 
											disabled={!addSelectedButtonEnabled}
											onClick={(e) => {
												addMultipleToMainStores(selectedStoreList);
												setSelectedStoreList([]);
											}}
											buttonText="Add to Main"
										/>
									</ZTooltip>
									
									<ZTooltip
										enabled={!addSelectedButtonEnabled} 
										text={addSelectedButtonError} 
										placement="top"  
									>
										<ZButton 
											className="ms-2" 
											disabled={!addSelectedButtonEnabled}
											onClick={(e) => {
												addMultipleToViewStores(selectedStoreList);
												setSelectedStoreList([]);
											}}
											buttonText="Add to View"
										/>
									</ZTooltip>
									
									<ZTooltip
										enabled={!addSelectedButtonEnabled} 
										text={addSelectedButtonError} 
										placement="top"  
									>
										<ZButton
											className="ms-2" 
											disabled={!addSelectedButtonEnabled}
											onClick={(e) => {
												addMultipleToMainStores(selectedStoreList);
												addMultipleToViewStores(selectedStoreList);
												setSelectedStoreList([]);
											}} 
											buttonText="Add to Both"
										/>
									</ZTooltip>
								</Col>
								<Col xs={4} />
								<Col xs={4}>
									<div style={{ display: 'flex' }}>
										<div style={{ flex: 1 }}></div>

										<div>
											<Button disabled={multistoreGroup.length === 0}
												onClick={() => setShowPrompt(true)}>
												Save
											</Button>
									</div>
								</div>
							</Col>
						</Row>
					</Card.Body>
				</Card>

				<Card>
					<Card.Body>
						<Row className="mb-1">
							<div style={{ maxHeight: '500px', overflowY: 'auto' }}>

								<div className="d-flex align-items-center">
									<h3 className="m-0">Multi-Store View</h3>
									<Button className="ms-2 py-0 px-1" variant="danger" onClick={() => setTestResult([])}>
										Clear All
									</Button>
								</div>


								<br />

								{testResult.map((item: any, index: number) => (
									<div>
										{item.storeId} - {item.storeName} - {item.errors.length > 0 ? item.errors.join(", ") : item.response && item.response.name + ' Ph:' + item.response.phoneNo}
									</div>
								))}
							</div>
						</Row>
					</Card.Body>
				</Card>
			</Col>
		</Row >

			<Modal
				title="Confirm - Save Multistore"
				show={showPrompt}
				buttonType="primary"
				disableButtons={false}
				buttonText={"Accept"}
				handleClose={() => setShowPrompt(false)}
				handleSubmit={() => {
					saveMultiStore();
					setShowPrompt(false);
				}}
				description={
					<>
						<Alert variant="danger">
							<div className="d-flex flex-row">
								<i className="mdi mdi-alert me-1"></i>
								<div>
									Data breaches can occur with the incorrect selection of stores/store groups.
									<br /><p className="m-0 mt-1 fw-bold">Please double-check your selection.</p>
								</div>
							</div>
						</Alert>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>

			<Modal
				title="Different Owners!"
				show={showDifferentOwnerPrompt}
				buttonType="primary"
				disableButtons={false}
				buttonText={"Accept"}
				handleClose={() => setShowDifferentOwnerPrompt(false)}
				handleSubmit={() => {
					differentOwnersPromptAction.current && differentOwnersPromptAction.current();
					setShowDifferentOwnerPrompt(false);
				}}
				description={
					<>
						<p>This store does not belong to any of the owners of the existing stores! Are you sure they want to see each other?</p>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>
		</>
	);
}



// 	const dispatch = useAppDispatch();
// 	const navigate = useNavigate();
// 	const stores = useAppSelector((state: RootState) => state.stores);
// 	const [storeList, setStoreList] = useState<any[]>([]);
// 	const [storeFilter, setStoreFilter] = useState<string>('');

// 	const [mainstores, setMainStores] = useState<any[]>([]);
// 	const [viewStores, setViewStores] = useState<any[]>([]);

// 	const [testResult, setTestResult] = useState<any[]>([]);

// 	const [showPrompt, setShowPrompt] = useState<boolean>(false);
// 	const [showDifferentOwnerPrompt, setShowDifferentOwnerPrompt] = useState<boolean>(false);
// 	const differentOwnersPromptAction = useRef<() => void>();

// 	useEffect(() => {
// 		dispatch(getStores({ limit: 10000 })).then((result) => {
// 			setStoreList(result.payload.data);
// 		});
// 	}, []);

// 	useEffect(() => {
// 		console.log("Main stores updated");
// 	}, [mainstores]);

// 	function onFilterChange(value: string) {
// 		var ids = value.split(' ').join(',').split(',');
// 		let ss: any[] = stores.table.filter((i: any) => i.name.toLowerCase().includes(value.toLowerCase()) || ids.includes(i.id.toString()));
// 		setStoreList(ss);
// 		setStoreFilter(value);
// 	}

// 	function editMainStore(store: any, clearMainStores: boolean = true) {
// 		clearMainStores && setMainStores([store])
// 		dispatch(GetViewableStores(store.id)).then((result) => {
// 			let ss: any[] = stores.table.filter((i: any) => result.payload.includes(i.id));
// 			setViewStores(ss)
// 		});
// 	}

// 	function addtoMainStores(store: any) {
// 		if (!mainstores.includes(store)) {
// 			let owners = [...mainstores.map(i => i.ownerId), ...viewStores.map(i => i.ownerId)];
// 			if (owners.length > 0 && !owners.includes(store.ownerId)) {
// 				differentOwnersPromptAction.current = () => setMainStores([...mainstores, store]);
// 				setShowDifferentOwnerPrompt(true);
// 			}
// 			else {
// 				setMainStores([...mainstores, store])
// 				differentOwnersPromptAction.current = undefined;
// 			}
// 		}
// 	}

// 	function addToViewStores(store: any) {
// 		if (!viewStores.includes(store)) {
// 			let owners = [...mainstores.map(i => i.ownerId), ...viewStores.map(i => i.ownerId)];
// 			if (owners.length > 0 && !owners.includes(store.ownerId)) {
// 				differentOwnersPromptAction.current = () => setViewStores([...viewStores, store]);
// 				setShowDifferentOwnerPrompt(true);
// 			}
// 			else {
// 				setViewStores([...viewStores, store])
// 				differentOwnersPromptAction.current = undefined;
// 			}

// 		}
// 	}

// 	function removeFromMainStores(store: any) {
// 		if (mainstores.includes(store)) {
// 			let temp = [...mainstores];
// 			temp.splice(temp.indexOf(store), 1);
// 			setMainStores(temp)
// 		}
// 	}

// 	function removeFromViewStores(store: any) {
// 		if (viewStores.includes(store)) {
// 			let temp = [...viewStores];
// 			temp.splice(temp.indexOf(store), 1);
// 			setViewStores(temp)
// 		}
// 	}

// 	function testMultiStore(stores: any[]) {
// 		if (stores.length > 0) {
// 			dispatch(MultiStoreTest(stores.map(i => i.id as number))).then((result) => {
// 				setTestResult(result.payload.stores);
// 				console.log(result.payload.stores);
// 			})
// 		} else {
// 			setTestResult([]);
// 		}
// 	}


// 	async function saveMultiStore() {
// 		let errors: any[] = [];

// 		if (mainstores.length > 0) {
// 			for (let index = 0; index < mainstores.length; index++) {
// 				const element = mainstores[index];

// 				await dispatch(UpdateMultiStore({ storeId: element.id, viewStoreIds: viewStores.map(i => i.id) })).then((result) => {
// 					console.log(result.payload);
// 					if (!result.payload?.data) {
// 						errors.push(result.payload.error);
// 					}
// 				});
// 			}
// 		}

// 		if (errors.length > 0) {
// 			dispatch(showToast({ value: errors }));
// 		} else {
// 			dispatch(showToast({ value: ["Done"] }));
// 		}
// 	}

// 	return (
// 		<>
// 			<Title pageTitle="MultiStore" parent="Support" />
// 			<Row>
// 				<Col xs={12}>
// 					<Card>
// 						<Card.Body>
// 							<Row className="mb-1">
// 								<Col xs={4}>
// 									<h3>Search</h3>

// 									<Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
// 										<Form.Control value={storeFilter} onChange={(e) => onFilterChange(e.target.value)} />
// 									</Form.Group>

// 									<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
// 										<ListGroup>
// 											{storeList.map((store: any, index: any) => (
// 												<ListGroup.Item key={index} action>
// 													<div style={{ display: 'flex', width: '100%' }}>
// 														{store.id} {store.name}

// 														<div style={{ flex: 1 }} />

// 														<div>
// 															<Button variant="primary" onClick={() => editMainStore(store)}>
// 																<i className="mdi mdi-pencil"></i>
// 															</Button>

// 															<Button variant="secondary" onClick={() => addtoMainStores(store)}>
// 																<i className="mdi mdi-numeric-1-box-outline"></i>
// 															</Button>

// 															<Button variant="info" onClick={() => addToViewStores(store)}>
// 																<i className="mdi mdi-numeric-2-box-outline"></i>
// 															</Button>
// 														</div>
// 													</div>
// 												</ListGroup.Item>
// 											))}
// 										</ListGroup>
// 									</div>
// 								</Col>

// 								<Col xs={4}>

// 									<h3>Main Stores</h3>

// 									<div style={{ height: '50px', display: 'flex', alignItems: 'center' }}>
// 										<Button variant="danger" onClick={() => setMainStores([])}>
// 											<i className="mdi mdi-trash-can"></i>
// 										</Button>

// 										<Button variant="secondary" onClick={() => testMultiStore(mainstores)}>
// 											<i className="mdi mdi-eye-check"></i>
// 										</Button>
// 									</div>


// 									<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
// 										<ListGroup>
// 											{mainstores.map((store: any, index: any) => (
// 												<ListGroup.Item key={index} action>
// 													<div style={{ display: 'flex', width: '100%' }}>
// 														{store.id} {store.name}

// 														<div style={{ flex: 1 }} />

// 														<div>
// 															<Button variant="info" onClick={() => addToViewStores(store)}>
// 																<i className="mdi mdi-numeric-2-box-outline"></i>
// 															</Button>

// 															<Button variant="danger" onClick={() => removeFromMainStores(store)}>
// 																<i className="mdi mdi-trash-can"></i>
// 															</Button>
// 														</div>

// 													</div>
// 												</ListGroup.Item>
// 											))}
// 										</ListGroup>
// 									</div>

// 								</Col>

// 								<Col xs={4}>
// 									<h3>View Stores</h3>

// 									<div style={{ height: '50px', display: 'flex', alignItems: 'center' }}>
// 										<Button variant="danger" onClick={() => setViewStores([])}>
// 											<i className="mdi mdi-trash-can"></i>
// 										</Button>

// 										<Button variant="secondary" onClick={() => testMultiStore(viewStores)}>
// 											<i className="mdi mdi-eye-check"></i>
// 										</Button>
// 									</div>


// 									<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
// 										<ListGroup>
// 											{viewStores.map((store: any, index: any) => (
// 												<ListGroup.Item key={index} action>
// 													<div style={{ display: 'flex', width: '100%' }}>
// 														{store.id} {store.name}

// 														<div style={{ flex: 1 }} />

// 														<div>
// 															<Button variant="primary" onClick={() => editMainStore(store)}>
// 																<i className="mdi mdi-pencil"></i>
// 															</Button>

// 															<Button variant="secondary" onClick={() => addtoMainStores(store)}>
// 																<i className="mdi mdi-numeric-1-box-outline"></i>
// 															</Button>

// 															<Button variant="danger" onClick={() => removeFromViewStores(store)}>
// 																<i className="mdi mdi-trash-can"></i>
// 															</Button>
// 														</div>
// 													</div>
// 												</ListGroup.Item>
// 											))}
// 										</ListGroup>
// 									</div>

// 								</Col>
// 							</Row>

// 							<Row>
// 								<div style={{ display: 'flex', flex: 1 }}>
// 									<div style={{ flex: 1 }}></div>

// 									<Button variant="primary" onClick={() => setShowPrompt(true)}>
// 										<i className="mdi mdi-content-save-settings"></i> Save
// 									</Button>
// 								</div>
// 							</Row>
// 						</Card.Body>
// 					</Card>

// 					<Card>
// 						<Card.Body>
// 							<Row className="mb-1">
// 								<div style={{ maxHeight: '500px', overflowY: 'auto' }}>

// 									<div style={{ display: "flex", flex: 1, alignItems: 'center' }}>

// 										<h3 style={{ margin: '5px' }}>Multistore View</h3>

// 										<Button variant="danger" onClick={() => setTestResult([])}>
// 											<i className="mdi mdi-trash-can"></i>
// 										</Button>
// 									</div>


// 									<br />

// 									{testResult.map((item: any, index: number) => (
// 										<div>
// 											{item.storeId} - {item.storeName} - {item.errors.length > 0 ? item.errors.join(", ") : item.response && item.response.name + ' Ph:' + item.response.phoneNo}
// 										</div>
// 									))}
// 								</div>
// 							</Row>
// 						</Card.Body>
// 					</Card>
// 				</Col>
// 			</Row>

// 			<Modal
// 				title="Confirm - Save Multistore"
// 				show={showPrompt}
// 				buttonType="primary"
// 				disableButtons={false}
// 				buttonText={"Accept"}
// 				handleClose={() => setShowPrompt(false)}
// 				handleSubmit={() => {
// 					saveMultiStore();
// 					setShowPrompt(false);
// 				}}
// 				description={
// 					<>
// 						<p>Data breaches can occur with the incorrect selection of stores/store groups.</p>
// 						<p>Please double-check your selection.</p>
// 						<p>Are you sure you want to continue?</p>
// 					</>
// 				}
// 			/>

// 			<Modal
// 				title="Different Owners!"
// 				show={showDifferentOwnerPrompt}
// 				buttonType="primary"
// 				disableButtons={false}
// 				buttonText={"Accept"}
// 				handleClose={() => setShowDifferentOwnerPrompt(false)}
// 				handleSubmit={() => {
// 					differentOwnersPromptAction.current && differentOwnersPromptAction.current();
// 					setShowDifferentOwnerPrompt(false);
// 				}}
// 				description={
// 					<>
// 						<p>This store does not belong to any of the owners of the existing stores! Are you sure they want to see each other?</p>
// 						<p>Are you sure you want to continue?</p>
// 					</>
// 				}
// 			/>
// 		</>
// 	);
// }